import { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS, it, es, fr, de } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar as CalendarIcon, Plus, Trash2, Clock } from 'lucide-react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const locales = { en: enUS, it, es, fr, de };

export default function AvailabilityCalendar({ userId, viewOnly = false }) {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [slots, setSlots] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBookDialog, setShowBookDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [newSlot, setNewSlot] = useState({
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    notes: ''
  });
  const [booking, setBooking] = useState({
    hours: 1,
    description: ''
  });
  const [loading, setLoading] = useState(false);

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), { locale: locales[i18n.language] || enUS }),
    getDay,
    locales,
  });

  useEffect(() => {
    fetchSlots();
  }, [userId]);

  const fetchSlots = async () => {
    try {
      const endpoint = userId 
        ? `/availability/slots/user/${userId}`
        : '/availability/slots/my';
      
      const response = await axios.get(`${API}${endpoint}`, { withCredentials: true });
      
      // Convert slots to calendar events
      const events = response.data.map(slot => ({
        id: slot.id,
        title: slot.is_booked ? t('calendar.booked') : t('calendar.available'),
        start: new Date(`${slot.date}T${slot.start_time}`),
        end: new Date(`${slot.date}T${slot.end_time}`),
        resource: slot,
        className: slot.is_booked ? 'booked-slot' : 'available-slot'
      }));
      
      setSlots(events);
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error(t('errors.generic'));
    }
  };

  const handleAddSlot = async () => {
    if (!newSlot.date || !newSlot.start_time || !newSlot.end_time) {
      toast.error(t('calendar.fillAllFields'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/availability/slots`, newSlot, { withCredentials: true });
      
      toast.success(t('calendar.slotAdded'));
      setShowAddDialog(false);
      setNewSlot({ date: '', start_time: '09:00', end_time: '17:00', notes: '' });
      fetchSlots();
    } catch (error) {
      console.error('Error adding slot:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/availability/slots/${slotId}`, { withCredentials: true });
      toast.success(t('calendar.slotDeleted'));
      fetchSlots();
    } catch (error) {
      console.error('Error deleting slot:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleBookSlot = async () => {
    if (!booking.description.trim() || booking.hours <= 0) {
      toast.error(t('errors.invalidInput'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/availability/book`, {
        slot_id: selectedSlot.id,
        hours: parseFloat(booking.hours),
        description: booking.description
      }, { withCredentials: true });
      
      toast.success(t('calendar.bookingConfirmed'));
      setShowBookDialog(false);
      setBooking({ hours: 1, description: '' });
      fetchSlots();
    } catch (error) {
      console.error('Error booking slot:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event) => {
    if (viewOnly && !event.resource.is_booked) {
      // Viewing someone else's calendar - can book
      setSelectedSlot(event.resource);
      setShowBookDialog(true);
    } else if (!viewOnly) {
      // Own calendar - can delete
      setSelectedSlot(event.resource);
    }
  };

  const eventStyleGetter = (event) => {
    const style = {
      backgroundColor: event.resource.is_booked ? '#94a3b8' : '#14b8a6',
      borderRadius: '5px',
      opacity: 0.9,
      color: 'white',
      border: '0px',
      display: 'block'
    };
    return { style };
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-6 w-6 text-teal-500" />
          <h3 className="text-xl font-bold">{viewOnly ? t('calendar.availability') : t('calendar.myAvailability')}</h3>
        </div>
        {!viewOnly && (
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-teal-500 hover:bg-teal-600 text-white"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('calendar.addSlot')}
          </Button>
        )}
      </div>

      {/* Legend */}
      <div className="flex gap-4 mb-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-teal-500 rounded"></div>
          <span>{t('calendar.available')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-slate-400 rounded"></div>
          <span>{t('calendar.booked')}</span>
        </div>
      </div>

      {/* Calendar */}
      <div style={{ height: '600px' }}>
        <Calendar
          localizer={localizer}
          events={slots}
          startAccessor="start"
          endAccessor="end"
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          views={['month', 'week', 'day']}
          defaultView="week"
          step={30}
          timeslots={2}
          culture={i18n.language}
        />
      </div>

      {/* Selected slot actions (own calendar) */}
      {!viewOnly && selectedSlot && !selectedSlot.is_booked && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{selectedSlot.date} at {selectedSlot.start_time} - {selectedSlot.end_time}</p>
              {selectedSlot.notes && <p className="text-sm text-gray-600">{selectedSlot.notes}</p>}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDeleteSlot(selectedSlot.id)}
              className="text-red-500 border-red-500 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {slots.length === 0 && (
        <p className="text-center text-gray-500 mt-8">
          {viewOnly ? t('calendar.noSlotsAvailable') : t('calendar.noSlotsYet')}
        </p>
      )}

      {/* Add Slot Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('calendar.addSlot')}</DialogTitle>
            <DialogDescription>{t('calendar.addSlotDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('calendar.date')}</label>
              <Input
                type="date"
                value={newSlot.date}
                onChange={(e) => setNewSlot({...newSlot, date: e.target.value})}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">{t('calendar.startTime')}</label>
                <Input
                  type="time"
                  value={newSlot.start_time}
                  onChange={(e) => setNewSlot({...newSlot, start_time: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">{t('calendar.endTime')}</label>
                <Input
                  type="time"
                  value={newSlot.end_time}
                  onChange={(e) => setNewSlot({...newSlot, end_time: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('calendar.notes')}</label>
              <Input
                placeholder={t('calendar.notesPlaceholder')}
                value={newSlot.notes}
                onChange={(e) => setNewSlot({...newSlot, notes: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleAddSlot} disabled={loading} className="bg-teal-500 hover:bg-teal-600">
              {loading ? t('common.saving') : t('calendar.addSlot')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Book Slot Dialog */}
      <Dialog open={showBookDialog} onOpenChange={setShowBookDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('calendar.bookSlot')}</DialogTitle>
            <DialogDescription>
              {selectedSlot && `${selectedSlot.date} at ${selectedSlot.start_time} - ${selectedSlot.end_time}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('calendar.hoursNeeded')}</label>
              <Input
                type="number"
                min="0.5"
                max={selectedSlot?.duration_hours || 8}
                step="0.5"
                value={booking.hours}
                onChange={(e) => setBooking({...booking, hours: e.target.value})}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('calendar.maxHours')}: {selectedSlot?.duration_hours || 0}h
              </p>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('calendar.description')}</label>
              <Input
                placeholder={t('calendar.descriptionPlaceholder')}
                value={booking.description}
                onChange={(e) => setBooking({...booking, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleBookSlot} disabled={loading} className="bg-teal-500 hover:bg-teal-600">
              {loading ? t('common.processing') : t('calendar.confirmBooking')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
